import React from "react";
import { Helmet } from "react-helmet-async";
import { DEFAULT_OG_IMAGE } from "../../utils/ogImage";
import { SITE_ORIGIN } from "../../utils/siteUrl";

// Site-wide default title/description/canonical/OG/Twitter tags, rendered
// once at the app root (see app.js). These used to be hardcoded directly in
// public/index.html; they moved here so react-helmet-async can manage and
// override them per-route (Guides/Places pages render their own <Seo>
// deeper in the tree — Helmet merges nested instances with the deeper one
// winning, so this component's values are just the fallback every other
// route keeps seeing exactly as before).
//
// One exception: when the page was loaded *directly* on a route the server
// injects <head> metadata for (see server/seo/injectHeadTags.js — the
// homepage, /about, the guides + places index and detail routes), that
// injected title/description/canonical is already in <head>. DefaultSeo
// renders once at the app root and never re-renders on navigation, so on
// such a direct load its generic <title>/canonical would be what Helmet
// applies during the loading-skeleton phase — replacing the correct
// injected values with the homepage ones for a beat until the page's own
// <Seo> mounts. Skipping the page-level tags here in that case leaves the
// injected values untouched (nothing wants a <title>, so document.title
// stays; the injected <link rel=canonical> stays), and the page's <Seo>
// re-asserts the identical values on mount. The genuinely global tags
// (locale, twitter:card) never conflict, so they always render.
//
// og:site_name is deliberately NOT here — it lives statically in
// public/index.html so it's in the initial server HTML for every route and
// there's exactly one of it (Helmet would append a second, non-adopted copy).
//
// Kept in sync with the route list registered in server.js. Excludes
// /places/:city/mood/:vibe (not server-injected — its <Seo> in
// PlacesListPage owns that metadata).
const isServerInjectedRoute = (p) => {
  const path = p.length > 1 ? p.replace(/\/+$/, "") : p;
  if (path === "/" || path === "/about" || path === "/guides") return true;
  if (/^\/(?:guides|places)\/[^/]+$/.test(path)) return true; // list-by-city
  if (/^\/(?:guides|places)\/[^/]+\/[^/]+$/.test(path) && !path.includes("/mood/")) return true; // detail
  return false;
};

const isDirectInjectedLoad = () => {
  if (typeof window === "undefined") return false;
  return isServerInjectedRoute(window.location.pathname);
};

const DefaultSeo = () => {
  const skipPageLevel = isDirectInjectedLoad();

  return (
    <Helmet>
      <meta property="og:locale" content="en_GB" />
      <meta name="twitter:card" content="summary_large_image" />

      {!skipPageLevel && (
        <title>Small Potatoes | Places Worth Knowing</title>
      )}
      {!skipPageLevel && (
        <meta name="description" content="Discover independent favourites, cultural spots and interesting places worth knowing in Southampton, Birmingham and London." />
      )}
      {!skipPageLevel && <link rel="canonical" href={`${SITE_ORIGIN}/`} />}
      {!skipPageLevel && <meta property="og:type" content="website" />}
      {!skipPageLevel && <meta property="og:url" content={`${SITE_ORIGIN}/`} />}
      {!skipPageLevel && (
        <meta property="og:title" content="Small Potatoes | Places Worth Knowing" />
      )}
      {!skipPageLevel && (
        <meta property="og:description" content="Get to know your city a little better. Independent favourites, culture and places worth knowing across Southampton, Birmingham and London." />
      )}
      {!skipPageLevel && <meta property="og:image" content={DEFAULT_OG_IMAGE} />}
      {!skipPageLevel && (
        <meta name="twitter:title" content="Small Potatoes | Places Worth Knowing" />
      )}
      {!skipPageLevel && (
        <meta name="twitter:description" content="Get to know your city a little better. Independent favourites, culture and places worth knowing across Southampton, Birmingham and London." />
      )}
      {!skipPageLevel && <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />}
    </Helmet>
  );
};

export default DefaultSeo;
